import Highcharts from 'highcharts';
import 'highcharts/modules/heatmap';
import GroupedCategories from 'highcharts-grouped-categories/grouped-categories';
import 'highcharts/modules/boost';
import '../../highcharts-options';
import Offcanvas from 'bootstrap/js/dist/offcanvas'

import './style.css'

if (typeof Highcharts === 'object') {
	GroupedCategories(Highcharts);
}


import { updateGauges } from '../../../components/charts/gauges';
import { getOverallScatterPlot } from '../scatter-plot-overall';
import * as utils from '../../../utils';

import { Aeroval } from '../../../types/global';
import { HMData, TrendsStats } from '../../../types/data';
import ranking from './ranking.json';

interface ExtendedGlobals extends Aeroval {
	sound: boolean;
	hide: string[];
}
declare var aeroval: ExtendedGlobals;

export function plotHeatmap(data: HMData, div: string, _chartOptions: any = {}): void {
	if (!aeroval.region) return;
	const region = aeroval.region;
	var dictModVar = {} as any;

	const currentSubPage = window.location.pathname.split('pages/')[1].split('/')[0]

	//prepare grid with proper number of pixels
	const xGrid = new Set<string>();
	const yGrid = new Set<string>();

	for (const [param, paramData] of Object.entries(data)) {

		if (
			aeroval.menu[param]?.only_use_in &&
			!aeroval.menu[param].only_use_in.includes(currentSubPage)
		) {
			continue;
		}

		if (!paramData) continue;

		for (const [obs, obsData] of Object.entries(paramData)) {
			for (const [layer, layerData] of Object.entries(obsData)) {

				yGrid.add(`${obs}:${param}_${layer}`);

				for (const [mod, modData] of Object.entries(layerData)) {
					const modVar = Object.keys(modData)[0];

					dictModVar[`${param}:${mod}`] = modVar;
					xGrid.add(mod);
				}
			}
		}
	}

	const hiddenExact = new Set(
		aeroval.hide.filter((x: string) => x.includes(':'))
	);

	const hiddenPartial = aeroval.hide.filter(
		(x: string) => !x.includes(':')
	);

	let xGridRecord = [...xGrid];

	const yGridRecord = [...yGrid].filter((obs) => {
		if (hiddenExact.has(obs)) return false;
		return !hiddenPartial.some(hidden => obs.includes(hidden));
	});

	//remove hidden elements from xGridRecord
	for (let j in aeroval.hide) {
		var element = aeroval.hide[j];
		//if not, remove if the string includes the element
		var filtered_mods = [];
		for (let i in xGridRecord) {
			var mod = xGridRecord[i] + '@';
			if (!mod.includes(element + '@')) {
				filtered_mods.push(xGridRecord[i]);
			}
		}
		xGridRecord = filtered_mods;
	}

	//time selection
	const keyTime = utils.getKeyTime(aeroval.time, aeroval.season);

	const seriesData = [];
	for (let i: number = 0; i < xGridRecord.length; i++) {
		for (let j: number = 0; j < yGridRecord.length; j++) {
			//from yGridRecord, separate var from obs from layer
			const obs = yGridRecord[j].split(':');
			const net = obs[0];
			const par = obs[1].split('_')[0];
			const lay = obs[1].split('_')[1];
			const mod = xGridRecord[i];
			const modVar = dictModVar[`${par}:${mod}`];

			if (!modVar) continue;

			let value: number | null = null;
			const subData = data?.[par]?.[net]?.[lay]?.[mod]?.[modVar]?.[region]?.[keyTime];


			// if score heatmap, show the score value
			if (aeroval.heatmapType === undefined || aeroval.heatmapType === 'score') {
				if (aeroval.statistic.dir === 'nrms') {
					value = (subData['rms'] as number) / Math.abs(subData['refdata_mean'] as number);
				} else if (subData[aeroval.statistic.dir]) {
					if (typeof subData[aeroval.statistic.dir] !== 'object') {
						value = subData[aeroval.statistic.dir] as number;
					} else {
						const trendStatistic = subData[aeroval.statistic.dir] as TrendsStats;
						const varMap = trendStatistic?.['map_var'] as string;
						if (varMap) {
							value = trendStatistic[varMap] as number;
						}
					}
				}

				// stats as percentages
				if (aeroval.statistic.unit == '%' && value != null) value *= 100;

				// fix decimal numbers
				if (value != null) value = parseFloat(value.toFixed(aeroval.statistic.decimals));
			} else {
				// if ranking heatmap, show the rank value: for each variable, we rank the models from best to worst, and show the rank of each model. The best model has rank 1, the second best rank 2, etc.
				// the ranking needs to be computed online based on the ordering (ascending or descending) of the statistic
				// the ranking needs to be done on absolute values for the statistic, to be fair between metrics where higher is better (eg r) and metrics where lower is better (eg bias)
				// if a model has no value, then the ranking should be null and appear as white in the heatmap
				const statDir = aeroval.statistic.dir;
				const useRoundedStats = true;
				if (statDir && statDir in ranking) {
					const modelEntries = Object.keys(data[par][net][lay]).map((modelName) => {
						const modelVarKey = dictModVar[`${par}:${modelName}`];
						const modelData = data[par][net][lay][modelName] as Record<string, any>;
						const modelStats = typeof modelVarKey !== 'string'
							? undefined
							: modelData[modelVarKey]?.[region]?.[keyTime] as Record<string, number | null | undefined> | undefined;
						let statValue = modelStats?.[statDir] ?? null;
						// stats as percentages
						if (aeroval.statistic.unit == '%' && statValue != null) statValue *= 100;

						// fix decimal numbers
						if (statValue != null && useRoundedStats) statValue = parseFloat(statValue.toFixed(aeroval.statistic.decimals));

						return {
							modelName,
							statValue: statValue,
						};
					});
					const currentModel = modelEntries.find((entry) => entry.modelName === mod);
					if (currentModel?.statValue !== null) {
						// we only work with models which are not into the list of aeroval.hide
						const sortedEntries = modelEntries
							.filter((entry) => entry.statValue != null && !aeroval.hide.includes(entry.modelName))
							.sort((a, b) => {
								if (ranking[statDir as keyof typeof ranking] === 'ascending') {
									return Math.abs(a.statValue as number) - Math.abs(b.statValue as number);
								}
								return Math.abs(b.statValue as number) - Math.abs(a.statValue as number);
							});

						const modelRanks = new Map<string, number>();
						const tieTolerance = Math.pow(10, -(aeroval.statistic.decimals + 1));
						let previousComparableValue: number | null = null;
						let previousRank = 0;

						sortedEntries.forEach((entry, index) => {
							const comparableValue = Math.abs(entry.statValue as number);
							const hasSameValue = useRoundedStats && previousComparableValue != null
								&& Math.abs(comparableValue - previousComparableValue) <= tieTolerance;
							const rank = hasSameValue ? previousRank : index + 1;
							modelRanks.set(entry.modelName, rank);
							previousComparableValue = comparableValue;
							previousRank = rank;
						});

						value = modelRanks.get(mod) ?? null;
					}
				}
			}



			seriesData.push({
				x: i,
				y: j,
				value: value,
				name: `${mod}, ${modVar}`,
				lay: lay,
				obs: net,
				var: par,
				mod: mod,
				modVar: modVar,
			});
		}
	}

	//unit
	const statUnit = aeroval.statistic.unit == '1' ? '' : `(${aeroval.statistic.unit})`;

	//prepare colorbar with n stops
	let scale: number[] = []
	const customMin = aeroval.chartOptions?.hm.min ? aeroval.chartOptions.hm.min : _chartOptions.min
	const customMax = aeroval.chartOptions?.hm.max ? aeroval.chartOptions.hm.max : _chartOptions.max
	const customStep = aeroval.chartOptions?.hm.step ? aeroval.chartOptions.hm.step : _chartOptions.step


	if (aeroval.heatmapType === undefined || aeroval.heatmapType === 'score') {
		if (customMin !== undefined && customMax !== undefined && customStep !== undefined) {
			scale = utils.linearArray(customMin, customMax, customStep)
		} else {
			scale = getScale(aeroval.statistic.dir)
		}
	} else {
		scale = utils.linearArray(1, xGridRecord.length, 1)
	}

	let legendTitle = ''
	let colorAxis = {
		min: 0,
		max: 99,
		minColor: '#FFFFFF',
		maxColor: '#FFFFFF',
		visible: false,
	} as any;

	if (aeroval.statistic.scale != null && aeroval.statistic.unit != 'var' || aeroval.heatmapType === 'ranking') {
		const stops = [];
		const nStops = 4;
		const min = scale[0];
		const max = scale[scale.length - 1];

		const statForColor = aeroval.heatmapType === 'ranking' ? 'fge' : aeroval.statistic.dir;
		for (let i: number = 0; i < nStops + 1; i++) {
			let value = min + (i * (max - min)) / nStops;
			stops.push([i / nStops, utils.colStat(value, scale, statForColor)]);
		}

		//colorAxis
		colorAxis = {
			stops,
			min,
			max,
			startOnTick: true,
			endOnTick: true,
			reversed: false,
		} as any;
		legendTitle = aeroval.heatmapType === 'ranking' ? `Ranking <br>(${aeroval.statistic.name})` : `${aeroval.statistic.name} ${statUnit}`;
	}

	//height of the chart: longer if more lines
	const minHeight = 400;
	const heightRow = 25;
	const height = Math.min(
		Math.max(yGridRecord.length * heightRow + 180, minHeight),
		window.innerHeight * 0.85
	);


	// prepare categories with variable in bold font
	var groups = [];
	var ids = [];
	const yCategories = [];
	for (let obs of yGridRecord) {
		const obs1 = obs.split(':')[1];
		const group = obs1.split('_')[0];
		groups.push(group);
		ids.push(obs1);
	}
	groups = groups.filter((x, i, a) => a.indexOf(x) == i);
	ids = ids.filter((x, i, a) => a.indexOf(x) == i);

	//initialize
	for (let group of groups) {
		yCategories.push({
			name: `<b class='${group}'>${utils.toSvgText(aeroval.menu[group].name)}</b>`,
			categories: [] as string[],
		});
	}

	for (let obs of yGridRecord) {
		const obs0 = obs.split(':')[0];
		const obs1 = obs.split(':')[1];
		const group = obs1.split('_')[0];
		const lay = obs1.split('_')[1];
		const cat = (lay === 'Surface' || lay === 'Column') ? obs0 : `${obs0}_${lay}`;
		const idx = groups.indexOf(group);
		yCategories[idx]['categories'].push(`<tspan class='${cat}:${obs1}'>${utils.toSvgText(cat)}</tspan>`);
	}

	// some parameters
	const marginLeft: number | undefined = aeroval.isMobile ? 200 : 320;
	const marginRight: number | undefined = aeroval.isMobile ? undefined : undefined;
	const spacingLeft = aeroval.isMobile ? 0 : 0;
	const spacingRight = aeroval.isMobile ? 0 : 0;
	const layout: Highcharts.OptionsLayoutValue = aeroval.isMobile ? 'horizontal' : 'vertical';
	const verticalAlign: Highcharts.VerticalAlignValue = aeroval.isMobile ? 'bottom' : 'middle';
	const align: Highcharts.AlignValue = aeroval.isMobile ? 'center' : 'right';
	const y = aeroval.isMobile ? 20 : -50;
	const allowOverlap = true;

	// tooltips and title
	const tooltip = (aeroval.sound || aeroval.settings.presentationMode === 'true') ? false : true;
	const title = aeroval.season != 'All' ? `${aeroval.region} - ${aeroval.time} (${aeroval.season})` : `${aeroval.region} - ${aeroval.time}`;

	const series = [
		{
			name: `${aeroval.statistic.name}-${aeroval.region}`,
			borderWidth: 1,
			borderColor: '#cccccc',
			data: seriesData,
			dataLabels: {
				enabled: true,
				color: '#000000',
				allowOverlap,
				filter: {
					operator: '<',
					property: 'value',
					value: '1E9'
				}
			}
		},
	] as any;

	// prepare matrice with nl and nc
	Highcharts.chart(div, {
		chart: {
			type: 'heatmap',
			marginTop: 60,
			marginBottom: 120,
			marginLeft,
			marginRight,
			spacingLeft,
			spacingRight,
			plotBorderWidth: 1,
			animation: {
				duration: 0,
			},
			height: height
		},
		boost: {
			allowForce: true,
			enabled: true,
		},
		title: {
			text: title,
			x: 50,
		},
		subtitle: {
			text: aeroval.frequency + ' data',
			x: 50,
		},
		xAxis: {
			categories: xGridRecord,
			labels: {
				useHTML: false,
			},
		},
		yAxis: {
			categories: yCategories,
			staticScale: heightRow,
			title: null,
			tickWidth: 0.5,
			reversed: true,
			labels: {
				useHTML: false,
				x: -5,
				style: {
					width: aeroval.isMobile ? 200 : 250
				},
				groupedOptions: [{
					x: aeroval.isMobile ? -10 : -70,
					style: {
						width: aeroval.isMobile ? 200 : 250
					}
				}]
			}
		} as any, // this axis is modified by the grouped categories module
		colorAxis,
		legend: {
			align,
			layout,
			margin: 0,
			verticalAlign,
			width: '100px',
			y: y,
			title: {
				text: legendTitle,
			},
			//symbolHeight: "100%"
		},
		exporting: {
			enabled: true,
			allowHTML: false,
			scale: 2,
			sourceHeight: undefined,
			sourceWidth: 800,
			chartOptions: {
				yAxis: [{
					categories: yCategories,
					tickWidth: 0.,
					reversed: true,
					labels: {
						useHTML: false,
						style: {
							useHTML: false,
							fontSize: "16px",
						}
					}
				} as any]
			}
		},
		tooltip: {
			useHTML: true,
			enabled: tooltip,
			animation: false,
			outside: false,
			style: {
				width: 200,
			},
			formatter: function () {
				// for some reason, the first row of the heatmap does not show the tooltip when testing if this.point.y exists
				if (!aeroval.sound) {
					var content = `Mod: <b>${this.name}</b> <br> Obs: <b>${this.series.yAxis.categories[(this as any).point.y]}</b> <br> ${aeroval.statistic.name
						}: <b>${this.value}${statUnit}</b>`;
					if (window.screen.width > 360 && !aeroval.sound) {
						content += `<div id='preview_scatterplot' style='height: 200px; width: 200px; border: 1px solid lightgray'></div><span style='font-size: smaller'>Click to expand the scatterplot.</span>`;
					}
					return `<div style="overflow:hidden">${content}</div>`;
				}
			},
		},
		plotOptions: {
			heatmap: {
				point: {
					events: {
						click: function () {
							if (aeroval.region && aeroval.sound !== true) {
								const selectedPixel = this as any;
								// Set cursor to waiting state
								document.body.style.cursor = 'progress';

								// Reduce heatmap and enlarge scatterplot
								const heatmap = document.getElementById('heatmap');
								if (heatmap) {
									heatmap.classList.remove('col-md-12');
									heatmap.classList.add('col-md-7');
								}

								const scatterplot = document.getElementById('scatterplot');
								if (scatterplot instanceof HTMLElement) {
									scatterplot.classList.remove('col-md-0');
									scatterplot.classList.add('col-md-5');
									scatterplot.style.display = 'initial';
								}

								const heatmapSpan = document.getElementById('heatmap-span');
								if (heatmapSpan instanceof HTMLElement) {
									heatmapSpan.style.display = 'none';
								}

								// reflow this chart
								this.series.chart.reflow();

								aeroval.statsForSelectedPixel = {
									n: data[selectedPixel.var][selectedPixel.obs][selectedPixel.lay][selectedPixel.mod][selectedPixel.modVar][aeroval.region][keyTime][
										'num_valid'
									] as number,
									nmb: data[selectedPixel.var][selectedPixel.obs][selectedPixel.lay][selectedPixel.mod][selectedPixel.modVar][aeroval.region][
										keyTime
									]['nmb'] as number,
									r: data[selectedPixel.var][selectedPixel.obs][selectedPixel.lay][selectedPixel.mod][selectedPixel.modVar][aeroval.region][keyTime][
										'R'
									] as number,
								};

								//update global variables
								getOverallScatterPlot(selectedPixel, true, false);

								// select this pixel
								this.select();

								//hide click on heatmap text
								const scatTxt = document.querySelector('#scatTxt');
								if (scatTxt instanceof HTMLElement) {
									scatTxt.style.display = 'none';
								}
							} else {
								//soundBox.playSound(Number(this.value))
							}
						},
						mouseOver: function () {
							const selectedPixel = this as any;
							if (window.screen.width > 360 && !aeroval.sound) {
								aeroval.wait = false;
								aeroval.timeOver = setTimeout(function () {
									if (!aeroval.wait && aeroval.settings.presentationMode !== 'true') {
										getOverallScatterPlot(selectedPixel, true, true);
									}
								}, 50);
								updateGauges(data, selectedPixel);
							}
						},
						mouseOut: function () {
							aeroval.wait = true;
							if (typeof aeroval.timeOver != 'undefined') {
								clearTimeout(aeroval.timeOver);
							}
						},
					},
				},
				cursor: 'pointer'
			},
		},
		series,
	}, function (thisChart: Highcharts.Chart) {
		/* Add events */
		const xAxisClickableElements = document.querySelectorAll(`#${div} .highcharts-xaxis-labels text`);
		const yAxisClickableElements = document.querySelectorAll(`#${div} .highcharts-yaxis-labels tspan[class]`);

		xAxisClickableElements.forEach((element) => {
			const getValue = (element: Element) =>
				element.children[0]
					? (element.children[0].className as string)
					: element.innerHTML;

			let clickTimer: number;

			// hide element if click
			element.addEventListener('click', () => {
				clearTimeout(clickTimer);

				clickTimer = window.setTimeout(() => {
					aeroval.hide.push(getValue(element));
					listHidden(div, data, true);
				}, 250);
			});

			// hide every other element if double click
			element.addEventListener('dblclick', () => {
				clearTimeout(clickTimer);
				const value = getValue(element);
				for (let i = 0; i < xGridRecord.length; i++) {
					const otherValue = xGridRecord[i];
					if (otherValue !== value) {
						aeroval.hide.push(otherValue);
					}
				}
				listHidden(div, data, true);
			});
		});

		yAxisClickableElements.forEach((element) => {
			const getValue = (element: Element) => element.getAttribute('class') ?? '';

			let clickTimer: number;

			// hide element if click
			element.addEventListener('click', () => {
				clearTimeout(clickTimer);

				clickTimer = window.setTimeout(() => {
					aeroval.hide.push(getValue(element));
					listHidden(div, data, true);
				}, 250);
			});

			// hide every other element if double click
			element.addEventListener('dblclick', () => {
				clearTimeout(clickTimer);
				const value = getValue(element);
				const isObservationNetwork = value.includes(':');
				const observationNetwork = isObservationNetwork ? value.split(':')[0] : '';
				// show value of all clickable elements
				// we loop over the grid directly, because some labels might not be displayed if the chart is too small, and we want to hide all the elements that are not the same as the clicked one
				for (let i = 0; i < yGridRecord.length; i++) {
					const otherValue = yGridRecord[i];
					const otherIsObservationNetwork = otherValue.includes(':');
					const otherObservationNetwork = otherIsObservationNetwork ? otherValue.split(':')[0] : '';
					if (isObservationNetwork && otherIsObservationNetwork && otherObservationNetwork !== observationNetwork) {
						aeroval.hide.push(otherValue);
					} else if (!isObservationNetwork) {
						if (!otherIsObservationNetwork && otherValue !== value) {
							aeroval.hide.push(otherValue);
						} else if (otherIsObservationNetwork && !otherValue.includes(value)) {
							aeroval.hide.push(otherValue);
						}
					}
				}
				listHidden(div, data, true);
			});
		});

		// add offcanvas event
		offcanvasLegend(thisChart, scale, data)
		if (aeroval.hide.length > 0) listHidden(div, data, false);
	});
}

function listHidden(div: string, data: HMData, redraw: boolean): void {
	let h = '';
	if (aeroval.hide.length > 0) {
		h = `<h6>
			Hidden Elements
			<div class='form-check form-check-inline'>
				<button class='btn btn-sm btn-${aeroval.settings.theme}' id='resetHidden'>
					<span class='bi bi-arrow-counterclockwise'></span>
				</button>
			</div>
		</h6>`;
		for (let element of aeroval.hide) {
			h += `<div class='form-check form-check-inline'>
			<input class='form-check-input' type='checkbox' value='${element}' id='hide-${element}' checked>
			<label class='form-check-label' for='hide-"${element}'>${element}</label>
			</div>`;
		}
	}
	const hiddenElements = document.getElementById('hideElements');
	if (hiddenElements) hiddenElements.innerHTML = h;

	// Get all elements with the class 'form-check-input'
	const checkboxes = document.querySelectorAll('input.form-check-input');
	checkboxes.forEach(function (checkbox) {
		checkbox.addEventListener('click', function (e) {
			const target = e.target as HTMLInputElement;
			const index = aeroval.hide.indexOf(target.value);
			if (index > -1) {
				aeroval.hide.splice(index, 1);
			}
			listHidden(div, data, true);
		});
	});

	// Add event listener to the reset button
	const resetButton = document.getElementById('resetHidden');
	if (resetButton) {
		resetButton.addEventListener('click', function () {
			aeroval.hide = [];
			listHidden(div, data, true);
		});
	}

	//redraw heatmap
	if (redraw) plotHeatmap(data, div);
}


function offcanvasLegend(chart: Highcharts.Chart, scale: number[], data?: HMData): void {

	const offcanvasId = `${chart.container.id}AxisOffcanvas`;
	const minScale = scale[0] ?? -1
	const maxScale = scale[scale.length - 1] ?? -1
	const stepScale = scale[1] - scale[0]

	let stepInput = utils.getLowerPowerOfTen(stepScale)
	// insures the step is small
	if (stepInput) stepInput /= 10

	// Create offcanvas content
	const offcanvasContent = `
			<div class="offcanvas offcanvas-end" tabindex="-1" id="${offcanvasId}" aria-labelledby="${offcanvasId}Label">
				<div class="offcanvas-header">
					<h5 class="offcanvas-title" id="${offcanvasId}Label">Colorscale configuration</h5>
					<button type="button" class="btn-close" data-bs-dismiss="offcanvas" aria-label="Close"></button>
				</div>
				<div class="offcanvas-body">
					<form id="${offcanvasId}Form">
						<div class="mb-3">
							<label for="${offcanvasId}minScale" class="form-label">Min</label>
							<input type="number" class="form-control" id="${offcanvasId}minScaleInput" value="${minScale}" step="${stepInput}"/>
						</div>
						<div class="mb-3">
							<label for="${offcanvasId}maxScale" class="form-label">Max</label>
							<input type="number" class="form-control" id="${offcanvasId}maxScaleInput" value="${maxScale}" step="${stepInput}"/>
						</div>
						<div class="mb-3">
							<label for="${offcanvasId}StepAxis" class="form-label">Step</label>
							<input type="number" class="form-control" id="${offcanvasId}stepScaleInput" value="${stepScale}" step="${stepInput}"/>
						</div>
						<button type="submit" class="btn btn-primary">Update chart</button>
					</form>
				</div>
			</div>
		`;

	// Create offcanvas element
	const offcanvasElement = document.createElement('div');
	offcanvasElement.innerHTML = offcanvasContent;
	document.body.appendChild(offcanvasElement);

	// Define event listener inside loop
	const activeElement = document.querySelector(`#${chart.container.id} .highcharts-legend g`)
	if (activeElement) activeElement.addEventListener("click", (function (offcanvasId) {
		return function () {
			const canvasElement = document.getElementById(offcanvasId);
			if (canvasElement instanceof HTMLElement) {
				const offcanvas = new Offcanvas(canvasElement);
				offcanvas.show();
			}
		};
	})(offcanvasId));

	// Add submit event listener to the form
	const form = document.getElementById(`${offcanvasId}Form`);
	if (!form) return;

	form.addEventListener('submit', function (event) {
		event.preventDefault(); // Prevent default form submission behavior

		// Get input elements
		const minInput = document.querySelector(`#${offcanvasId}minScaleInput`) as HTMLInputElement;
		const maxInput = document.querySelector(`#${offcanvasId}maxScaleInput`) as HTMLInputElement;
		const stepInput = document.querySelector(`#${offcanvasId}stepScaleInput`) as HTMLInputElement;

		// Get min and max values
		const min = parseFloat(minInput.value);
		const max = parseFloat(maxInput.value);
		const step = parseFloat(stepInput.value);

		// Update axis only if min or max values are valid
		if (!isNaN(min) || !isNaN(max)) {
			if (!aeroval.chartOptions) aeroval.chartOptions = {}
			aeroval.chartOptions.hm = { min: min, max: max, step: step }
			if (data) plotHeatmap(data, chart.container.id, aeroval.chartOptions.hm)
		}
	});
}

function getScale(statisticDir: string): number[] {
	let scale: number[] = []
	if (aeroval.statistics[statisticDir].scale) {
		if (aeroval.statistics[statisticDir].unit !== 'var') {
			scale = aeroval.statistics[statisticDir].scale;
		}
	}
	return scale;
}